import { Component, inject, OnInit, signal, ViewChildren, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { DateRange, DefaultMatCalendarRangeStrategy, MAT_DATE_RANGE_SELECTION_STRATEGY, MatCalendar, MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { SelectOption } from "@grupo-constant/next-dtype/types";
import { TranslocoModule } from "@ngneat/transloco";
import { compareAsc, differenceInCalendarMonths, getYear, lastDayOfMonth, parse, startOfToday } from "date-fns";
import { PipesModule } from "../../core/pipes/pipes.module";
import { NextCalendar } from "../../modules/admin/projects/projects.types";

@Component({
    selector: 'app-absence-calendar-dialog',
    templateUrl: './absence-calendar-dialog.component.html',
    styleUrls: ['./absence-calendar-dialog.component.scss'],
    imports: [FormsModule, PipesModule, MatButtonModule, MatCardModule, MatDatepickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressBarModule, MatSelectModule, MatSlideToggleModule, ReactiveFormsModule, TranslocoModule],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
          provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
          useClass: DefaultMatCalendarRangeStrategy,
        },
      ],
})
export class AbsenceCalendarDialogComponent implements OnInit {

    @ViewChildren(MatCalendar) calendarMatrix: MatCalendar<Date>[];

    year = getYear(startOfToday());
    disableNextYear = false;
    selectionDateRange: DateRange<Date> = undefined;

    months = signal<string[][]>([]);

    daysSelected: string[] = [];
    rangesSelected: { start: string, end: string }[] = [];
    weekdaysOption: SelectOption[] = [];
    calendarGroups = new Map<string, NextCalendar[]>();
    readonly = false;
    loading = true;

    public dialogRef = inject(MatDialogRef<AbsenceCalendarDialogComponent>);

    async ngOnInit() {
        this.months.set(this.getMonthsMatrix());
        this.loading = false;
    }

    dateFilter = (d: Date | null): boolean => {
        if (this.readonly) {
            return false;
        }
        if (differenceInCalendarMonths(new Date(), d) > 1) {
            return false;
        }
        return true;
    }

    isSelected = (event: Date) => {
        const date =
            event.getFullYear() +
            "-" +
            ("00" + (event.getMonth() + 1)).slice(-2) +
            "-" +
            ("00" + event.getDate()).slice(-2);
        return this.daysSelected.find(x => x == date) ? 'selected' : null;
    };

    isInRange = (event: Date) => {
        const date =
            event.getFullYear() +
            "-" +
            ("00" + (event.getMonth() + 1)).slice(-2) +
            "-" +
            ("00" + event.getDate()).slice(-2);
        return this.rangesSelected.find(x => {
            const start = parse(x.start, 'yyyy-MM-dd', startOfToday());
            const end = parse(x.end, 'yyyy-MM-dd', startOfToday());
            return compareAsc(event, start) >= 0 && compareAsc(event, end) <= 0;
        }) ? 'selected' : null;
    }

    select(event: Date, calendar: MatCalendar<Date>) {
        if (this.selectionDateRange && this.selectionDateRange.start && event >= this.selectionDateRange.start && !this.selectionDateRange.end) {
            this.selectionDateRange = new DateRange(this.selectionDateRange.start, event);
        } else {
            this.selectionDateRange = new DateRange(event, undefined);
        }
        if (this.selectionDateRange.start && this.selectionDateRange.end) {
            const start = this.selectionDateRange.start.getFullYear() +
                "-" +
                ("00" + (this.selectionDateRange.start.getMonth() + 1)).slice(-2) +
                "-" +
                ("00" + this.selectionDateRange.start.getDate()).slice(-2);
            const end = this.selectionDateRange.end.getFullYear() +
                "-" +
                ("00" + (this.selectionDateRange.end.getMonth() + 1)).slice(-2) +
                "-" +
                ("00" + this.selectionDateRange.end.getDate()).slice(-2);
            const index = this.rangesSelected.findIndex(x => x.start === start && x.end === end);
            if (index < 0) this.rangesSelected.push({ start, end });
            else this.rangesSelected.splice(index, 1);
            this.selectionDateRange = undefined;
        }
        // const index = this.daysSelected.findIndex(x => x == date);
        // if (index < 0) this.daysSelected.push(date);
        // else this.daysSelected.splice(index, 1);
        calendar.updateTodaysDate();
    }

    previousYear() {
        this.year = this.year - 1;
        this.months.set(this.getMonthsMatrix());
        this.disableNextYear = this.year - getYear(new Date()) >= 5;
    }

    nextYear() {
        this.year = this.year + 1;
        this.months.set(this.getMonthsMatrix());
        this.disableNextYear = this.year - getYear(new Date()) >= 5;
    }

    getStartDate(month: string) {
        return new Date(this.year, Number(month.substring(0, 2)) - 1, 1);
    }

    getMaxDate(month: string) {
        return lastDayOfMonth(new Date(this.year, Number(month.substring(0, 2)) - 1, 1));
    }

    getMinDate(month: string) {
        return new Date(this.year, Number(month.substring(0, 2)) - 1, 1)
    }

    closeDialog() {
        this.dialogRef.close();
    }

    async accept() {
        this.dialogRef.close();
    }

    private getMonthsMatrix() {
        return [
            ['01' + this.year, '02' + this.year, '03' + this.year, '04' + this.year],
            ['05' + this.year, '06' + this.year, '07' + this.year, '08' + this.year],
            ['09' + this.year, '10' + this.year, '11' + this.year, '12' + this.year]
        ];
    }
}